'use client';

import { useMemo } from 'react';
import { sumDomainTreeSelectionSignalCounts } from '@terra/ui-design-system';
import {
  useAppTargetId,
  useTargetId,
  useTargetType,
} from '../../../store/projectStore';
import { useAiSignalsKindFilter } from '../../../hooks/useAiSignalsKindFilter';
import { useArchivedLifecycleFilter } from '../../../hooks/useArchivedLifecycleFilter';
import { useTabScopedSignalsFilters } from '../../../store/tab/signalsTabScopedFilterStore';
import { useTabScopedDomainTree } from '../../../store/tab/domainTreeTabScopedFilterStore';
import {
  useDomainTreeFiltering,
  useTabDomainTreeFiltering,
} from '../../../contexts/SharedDomainTreeContext';
import { useDomainTreePanelCounts } from '../../../hooks/layout/useDomainTreePanelCounts';
import type { SignalsListScope } from './signals-list-filters.model';

export type SignalsFilterScope = 'application' | 'endpoint';

const toNonEmpty = (values: string[] | undefined) =>
  values && values.length > 0 ? values : undefined;

export function useSignalsScope({
  endpointId,
  filterScope,
}: {
  endpointId: string | undefined;
  filterScope: SignalsFilterScope;
}) {
  const targetId = useTargetId();
  const targetType = useTargetType();
  const appTargetId = useAppTargetId();
  const { selectedNodes } = useTabScopedDomainTree();
  const { lifecycleState } = useTabScopedSignalsFilters();
  const { lifecycleQueryParam } = useArchivedLifecycleFilter({
    lifecycleState,
  });
  const aiKindFilter = useAiSignalsKindFilter({
    targetId,
    targetType,
  });
  const { isAiApplication } = aiKindFilter;
  const isApplicationScope = filterScope === 'application';
  const domainTreeFilteringScope =
    isAiApplication || !isApplicationScope ? 'none' : 'application';
  const {
    domainTree,
    isFetching: isDomainTreeFetching,
    refetch: refetchDomainTree,
  } = useDomainTreeFiltering(domainTreeFilteringScope);
  const { nodeIdToCountMap, isRefreshing: isDomainTreeCountsRefreshing } =
    useDomainTreePanelCounts({
      appTargetId,
      shouldApplyAppLevel: isApplicationScope && !isAiApplication,
      isExecutionsTab: false,
      domainTree,
      isDomainTreeFetching,
      refetchDomainTree,
      lifecycleState: lifecycleQueryParam,
    });
  const treeFiltering = useTabDomainTreeFiltering({
    scope: domainTreeFilteringScope,
    selectedNodes,
  });
  const treeEndpointIds = toNonEmpty(treeFiltering.endpointIds);
  const treeUrlPatterns = treeEndpointIds
    ? undefined
    : toNonEmpty(treeFiltering.urlPatterns);
  const isTreeScope = !isAiApplication && isApplicationScope;
  const scope = useMemo<SignalsListScope>(
    () => ({
      endpointIds: isTreeScope ? treeEndpointIds : undefined,
      urlPatterns: isTreeScope ? treeUrlPatterns : undefined,
      componentTypes: isAiApplication ? aiKindFilter.componentTypes : undefined,
      includeApplicationLevelGroup: isAiApplication
        ? aiKindFilter.includeApplicationLevelGroup
        : isApplicationScope
          ? treeFiltering.includeApplicationLevelGroup
          : undefined,
    }),
    [
      aiKindFilter.componentTypes,
      aiKindFilter.includeApplicationLevelGroup,
      isAiApplication,
      isApplicationScope,
      isTreeScope,
      treeEndpointIds,
      treeFiltering.includeApplicationLevelGroup,
      treeUrlPatterns,
    ],
  );
  const hasScopedDomainTreeFilter =
    !isAiApplication &&
    (Boolean(scope.endpointIds?.length) ||
      Boolean(scope.urlPatterns?.length) ||
      scope.includeApplicationLevelGroup === true);
  const domainTreeScopedTotalCount = useMemo(
    () =>
      !hasScopedDomainTreeFilter || isDomainTreeCountsRefreshing
        ? undefined
        : sumDomainTreeSelectionSignalCounts({
            selectedNodeIds: selectedNodes,
            nodeIdToCountMap,
            domainTree,
          }),
    [
      domainTree,
      hasScopedDomainTreeFilter,
      isDomainTreeCountsRefreshing,
      nodeIdToCountMap,
      selectedNodes,
    ],
  );

  return {
    scope,
    isScopeFilterPending: isAiApplication
      ? aiKindFilter.isFilterPending
      : treeFiltering.isFilterPending,
    hasScopedDomainTreeFilter,
    domainTreeScopedTotalCount,
    aiKindNav: {
      isAiApplication,
      selectedKind: aiKindFilter.selectedKind,
      setSelectedKind: aiKindFilter.setSelectedKind,
      profile: aiKindFilter.profile,
    },
  };
}

export type SignalsScopeModel = ReturnType<typeof useSignalsScope>;
